import { useCallback, useRef, useState } from 'react';
import { sprintf } from 'sprintf-js';

import { ApiFileUploadTypes } from 'api/types/ApiFileUploadTypes';
import { Box } from 'components/Box';
import { Icon } from 'components/Icon/Icon';
import { LoadingDots } from 'components/LoadingDots/LoadingDots';
import { BodySmall } from 'components/Text/BodySmall';
import { S3Picker } from 'containers/S3Picker';
import { useViewportSizeEffect } from 'hooks/viewport/useViewportSizeEffect';
import { colors, transition } from 'theme/theme';
import { getRgbaString } from 'utils/style';

import { AspectRatioSvg, DropBox, DropContainer } from './FilePicker.styled';
import { Filename } from './Filename';

type Props = {
  downloadAttachment?: boolean;
  fileId: string | undefined;
  fileType: ApiFileUploadTypes;
  message?: string;
  name: string;
  onChange: (fileId: string | undefined) => void;
  onError?: (error: Error) => void;
  hideRemove?: boolean;
  isRequired?: boolean;
  'data-qa-id': string;
  'aria-label'?: string;
  hasError?: boolean;
  large?: boolean;
  showDate?: boolean;
  accept?: string;
  disabled?: boolean;
  extraAnalyticsData?: Record<string, unknown>;
  maxSize?: number; // in MB
};

export function FilePicker({
  downloadAttachment,
  fileId,
  fileType,
  message = getText('Choose a file or drag it here'),
  name,
  onChange,
  onError,
  'data-qa-id': qaId,
  hideRemove,
  isRequired = false,
  'aria-label': ariaLabel,
  hasError,
  large,
  showDate,
  accept,
  disabled,
  extraAnalyticsData,
  maxSize,
  ...props
}: Props) {
  const dropboxRef = useRef<HTMLDivElement>(null);
  const [aspectRatio, setAspectRatio] = useState<number>();

  useViewportSizeEffect(
    useCallback(() => {
      if (dropboxRef?.current) {
        const box = dropboxRef.current.getBoundingClientRect();
        setAspectRatio(box.width / box.height);
      }
    }, []),
  );

  const helpText = maxSize
    ? sprintf(getText('No file chosen (maximum size: %(maxSize)s MB)'), {
        maxSize,
      })
    : getText('No file chosen');

  return (
    <S3Picker
      downloadAttachment={downloadAttachment}
      isRequired={isRequired}
      fileId={fileId}
      fileType={fileType}
      name={name}
      onChange={onChange}
      onError={onError}
      qaId={qaId}
      aria-label={ariaLabel}
      accept={accept}
      disabled={disabled}
      extraAnalyticsData={extraAnalyticsData}
      maxSize={maxSize}
    >
      {({ onClick, filename, created, loading, dragActive, mouseActive }) => (
        <DropContainer
          position="relative"
          display="inline-flex"
          width={1}
          flexWrap="wrap"
          alignItems="center"
          pointerEvents="none"
          $dragActive={dragActive}
          $mouseActive={mouseActive}
          $large={large}
        >
          <Box
            position="absolute"
            left={-12}
            right={-12}
            top={-12}
            bottom={-12}
            borderRadius={large ? '16px' : '4px'}
            bg={`rgba(${getRgbaString(colors.skyBlue, 0.2)})`}
            opacity={mouseActive && !loading ? 1 : 0}
            transition={`opacity ${transition}`}
          />
          {loading && !large ? (
            <Box
              width={32}
              height={65}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <LoadingDots size="small" />
            </Box>
          ) : (
            <>
              {large ? (
                <Box width={1} flex="0 0 auto">
                  <DropBox
                    position="relative"
                    onClick={onClick}
                    $hasFile={Boolean(fileId)}
                    $large
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                  >
                    <div
                      ref={dropboxRef}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        height: '192px',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '6px 16px',
                      }}
                    >
                      {aspectRatio && (
                        <AspectRatioSvg
                          viewBox={`0 0 ${100 * aspectRatio} 100`}
                        >
                          <rect
                            x={1}
                            y={1}
                            width={100 * aspectRatio - 2}
                            height={98}
                          />
                        </AspectRatioSvg>
                      )}

                      {loading ? (
                        <Box
                          width={1}
                          height={192}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <LoadingDots size="default" />
                        </Box>
                      ) : (
                        <>
                          <Box position="relative">
                            <Icon
                              name="paperclip"
                              size={32}
                              colorName="brandBlue"
                            />
                          </Box>
                          <Box
                            position="relative"
                            ml={12}
                            fontSize={[20, null, null, 24]}
                          >
                            {fileId ? (
                              <Filename
                                fileId={fileId}
                                filename={filename}
                                qaId={qaId}
                                hideRemove={hideRemove}
                                onChange={onChange}
                                created={showDate ? created : undefined}
                                downloadAttachment={downloadAttachment}
                                disabled={disabled}
                                large
                              />
                            ) : (
                              message
                            )}
                          </Box>
                        </>
                      )}
                    </div>
                  </DropBox>
                </Box>
              ) : (
                <Box data-qa-id={`${qaId}-selector`} mx={-10}>
                  <DropBox
                    position="relative"
                    p={10}
                    onClick={onClick}
                    $hasFile={Boolean(fileId)}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                  >
                    <Box display="flex" alignItems="center" p="6px 16px">
                      <Box ml="4px" mr="8px" display="inline-flex">
                        <Icon name="paperclip" size={24} />
                      </Box>
                      <BodySmall my={0}>{message}</BodySmall>
                    </Box>
                  </DropBox>
                </Box>
              )}

              {!large && (
                <Box
                  display="inline-block"
                  ml={24}
                  color={colors.lightContentGrey}
                >
                  {fileId ? (
                    <Filename
                      fileId={fileId}
                      filename={filename}
                      qaId={qaId}
                      hideRemove={hideRemove}
                      onChange={onChange}
                      created={showDate ? created : undefined}
                      downloadAttachment={downloadAttachment}
                      disabled={disabled}
                    />
                  ) : (
                    helpText
                  )}
                </Box>
              )}
            </>
          )}
        </DropContainer>
      )}
    </S3Picker>
  );
}
